// src/components/sprites/TickerIcon.jsx
import React, { useEffect, useState } from 'react'; // add useState
import { useTheme } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import { brandTokens } from '@softwareiq/brand-ui';
import manifest from '../../data/spritesManifest.json';
import { CDN_BASE_URL } from '../../constants/api';
import {
  getCompanyDisplayName,
  isPrivateCompanyIdentifier,
} from '../../utils/companyHelpers';

const SPRITE_BASE_URL = manifest.spriteBaseUrl || CDN_BASE_URL;
const SPRITE_BASE_PATH = manifest.spriteBasePath || '/sprite'; // icons.svg & logos.svg live here
const COMPANY_ASSET_BASE_PATH = '/sprite/logos';

// TICKER -> kebab id used in sprites
function spriteIdFromTicker(t = '') {
  return String(t).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function assetTickerFromTicker(t = '') {
  return String(t).toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function appendVersion(url, version) {
  if (!url || !version || /[?&]v=/.test(url)) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${encodeURIComponent(version)}`;
}

function toUrl(file, base = SPRITE_BASE_URL, basePath = SPRITE_BASE_PATH, version) {
  if (!file) return null;
  if (/^https?:\/\//i.test(file)) return appendVersion(file, version);
  if (file.startsWith('/')) return appendVersion(`${base}${file}`, version);

  const normalizedBasePath = String(basePath || '').replace(/^\/+|\/+$/g, '');
  if (normalizedBasePath && file.startsWith(`${normalizedBasePath}/`)) {
    return appendVersion(`${base}/${file}`, version);
  }
  const url = normalizedBasePath
    ? `${base}/${normalizedBasePath}/${file}`
    : `${base}/${file}`;
  return appendVersion(url, version);
}

/* ------------------------------ DOM bank (for CDN) ------------------------ */
function ensureBank() {
  if (typeof document === 'undefined') return null;
  let bank = document.getElementById('__svg-sprite-bank');
  if (!bank) {
    bank = document.createElement('div');
    bank.id = '__svg-sprite-bank';
    bank.style.position = 'absolute';
    bank.style.width = '0';
    bank.style.height = '0';
    bank.style.overflow = 'hidden';
    bank.style.clipPath = 'inset(100%)';
    document.body.appendChild(bank);
  }
  return bank;
}

const injectedByUrl = new Set();
const inflightByUrl = new Map();

function stripSpriteStyleElements(svgText = '') {
  return String(svgText).replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
}

function injectSpriteOnce(url) {
  if (!url || typeof document === 'undefined') return Promise.resolve();
  if (injectedByUrl.has(url)) return Promise.resolve();
  if (inflightByUrl.has(url)) return inflightByUrl.get(url);

  const p = fetch(url, { mode: 'cors', credentials: 'omit' })
    .then(r => {
      if (!r.ok) throw new Error(`Failed to fetch ${url}: ${r.status}`);
      return r.text();
    })
    .then(txt => {
      const bank = ensureBank();
      if (!bank) return;
      if (bank.querySelector(`[data-sprite-url="${url}"]`)) {
        injectedByUrl.add(url);
        return;
      }
      const host = document.createElement('div');
      host.setAttribute('data-sprite-url', url);
      host.innerHTML = DOMPurify.sanitize(stripSpriteStyleElements(txt));
      bank.appendChild(host);
      injectedByUrl.add(url);
    })
    .catch(() => { /* swallow to avoid breaking render */ })
    .finally(() => {
      inflightByUrl.delete(url);
    });

  inflightByUrl.set(url, p);
  return p;
}

/* ------------------------------ manifest urls ----------------------------- */
// You provided these keys in your manifest:
const SPRITE_VERSIONS = {
  icons: {
    light: manifest.iconsLightVersion || manifest.iconsVersion || manifest.spriteVersion,
    dark: manifest.iconsDarkVersion || manifest.iconsVersion || manifest.spriteVersion,
  },
  logos: {
    light: manifest.logosLightVersion || manifest.logosVersion || manifest.spriteVersion,
    dark: manifest.logosDarkVersion || manifest.logosVersion || manifest.spriteVersion,
  },
};

const SPRITE_URLS = {
  icons: {
    light: toUrl(manifest.iconsLight || manifest.iconsBoth || manifest.light || 'icons.svg', undefined, undefined, SPRITE_VERSIONS.icons.light),
    dark: toUrl(manifest.iconsDark || manifest.iconsBoth || manifest.dark || 'icons.svg', undefined, undefined, SPRITE_VERSIONS.icons.dark),
  },
  logos: {
    light: toUrl(manifest.logosLight || manifest.logosBoth || 'logos.svg', undefined, undefined, SPRITE_VERSIONS.logos.light),
    dark: toUrl(manifest.logosDark || manifest.logosBoth || 'logos.svg', undefined, undefined, SPRITE_VERSIONS.logos.dark),
  },
};

// Optional allow-lists (nice-to-have)
const ICON_IDS = Array.isArray(manifest.icons) ? new Set(manifest.icons) : null;
const LOGO_IDS = Array.isArray(manifest.logos) ? new Set(manifest.logos) : null;

function decideRole(prefer, id) {
  if (prefer === 'icons' || prefer === 'logos') return prefer;
  const inIcons = ICON_IDS ? ICON_IDS.has(id) : true;
  const inLogos = LOGO_IDS ? LOGO_IDS.has(id) : false;
  return inIcons ? 'icons' : (inLogos ? 'logos' : 'icons');
}

function hasSpriteId(role, id) {
  const ids = role === 'logos' ? LOGO_IDS : ICON_IDS;
  return ids ? ids.has(id) : Boolean(id);
}

// Build symbol id exactly like your sprite: role-<mode>--<id>
function buildSymbolId(role, mode, id) {
  // role: "icons" | "logos" ; mode: "light" | "dark"
  return `${role}-${mode}--${id}`;
}

function resolveSpriteUrl({ role, mode, spriteUrl, spriteUrls }) {
  const version = SPRITE_VERSIONS[role]?.[mode];
  if (typeof spriteUrl === 'string') return toUrl(spriteUrl, undefined, undefined, version);
  if (spriteUrls && typeof spriteUrls === 'object') {
    const roleValue = spriteUrls[role];
    if (typeof roleValue === 'string') return toUrl(roleValue, undefined, undefined, version);
    if (roleValue && typeof roleValue === 'object') {
      return toUrl(roleValue[mode] || roleValue.light || roleValue.dark, undefined, undefined, version);
    }
  }
  return SPRITE_URLS[role]?.[mode] || SPRITE_URLS[role]?.light || null;
}

function resolveCompanyAssetUrl({ ticker, prefer, mode }) {
  const assetTicker = assetTickerFromTicker(ticker);
  if (!assetTicker) return null;

  const isLogo = prefer === 'logos' || prefer === 'logo';
  const file = isLogo
    ? (mode === 'dark' ? 'logodark.svg' : 'logo.svg')
    : (mode === 'dark' ? 'icondark.svg' : 'icon.svg');

  return `${CDN_BASE_URL}${COMPANY_ASSET_BASE_PATH}/${assetTicker}/${file}`;
}

/* --------------------------- optional text fallback ----------------------- */
function buildFallbackText(raw = '') {
  const label = String(raw).trim();
  if (!label) return '?';

  // If 4 or fewer characters, keep as-is (old behavior)
  if (label.length <= 4) return label.toUpperCase();

  // Split into words: handles spaces, punctuation, dashes, underscores.
  // Also filters out the word "blog" (case-insensitive).
  const words = label
    .split(/[^A-Za-z0-9]+/g)
    .filter(Boolean)
    .filter(w => w.toLowerCase() !== 'blog');

  // If we ended up with nothing (e.g., label was only "blog"), fallback to first 4 chars
  if (words.length === 0) return label.slice(0, 4).toUpperCase();

  // First letter of each word, join; cap to 4 to avoid overflowing the square
  const initials = words.map(w => w[0]).join('').toUpperCase();
  return initials.slice(0, 4);
}
function hasSymbolInBank(symbolId) {
  if (typeof document === 'undefined') return false;
  const bank = document.getElementById('__svg-sprite-bank');
  if (!bank) return false;
  const safe = CSS?.escape ? CSS.escape(symbolId) : symbolId.replace(/"/g, '\\"');
  return !!bank.querySelector(`#${safe}`);
}
/* --------------------------- optional text fallback ----------------------- */
function TextPlaceholder({ size, width, height, label, className, style, title }) {
  const text = buildFallbackText(label) || '?';
  const renderWidth = width ?? size;
  const renderHeight = height ?? size;

  // Scale font a bit when we have more letters (e.g., 3–4 initials)
  const maxChars = Math.max(1, text.length);
  const base = 0.42;                // original proportion
  const scale = Math.min(1, 4 / maxChars);
  const fontSize = Math.max(10, Math.floor(size * base * scale));

  return (
    <svg role={title ? 'img' : undefined} aria-label={title || undefined}
         width={renderWidth} height={renderHeight} className={className} style={style}
         viewBox={`0 0 ${renderWidth} ${renderHeight}`} focusable="false">
      <rect x="0" y="0" width={renderWidth} height={renderHeight} rx="4" ry="4" fill="currentColor" opacity="0.08" />
      <text x={renderWidth / 2} y={(renderHeight / 2) + (fontSize * 0.34)} textAnchor="middle" fontFamily={brandTokens.fontFamily} fontSize={fontSize} fill="currentColor">
        {text}
      </text>
    </svg>
  );
}

function CdnTickerAsset({
  url,
  size,
  width,
  height,
  title,
  className,
  style,
  opacity,
  onError,
}) {
  const svgStyle = opacity == null ? style : { ...style, opacity };
  const renderWidth = width ?? size;
  const renderHeight = height ?? size;

  return (
    <svg
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      width={renderWidth}
      height={renderHeight}
      className={className}
      style={svgStyle}
      viewBox={`0 0 ${renderWidth} ${renderHeight}`}
      focusable="false"
    >
      {title && <title>{title}</title>}
      <image
        href={url}
        xlinkHref={url}
        x="0"
        y="0"
        width={renderWidth}
        height={renderHeight}
        preserveAspectRatio="xMidYMid meet"
        onError={onError}
      />
    </svg>
  );
}

/* -------------------------------- SpriteIcon ------------------------------ */
export function SpriteIcon({
  id,
  prefer = 'icons',
  mode,
  spriteUrl,
  spriteUrls,
  fallback = 'none',
  size = 24,
  width,
  height,
  title,
  label,
  className,
  style,
  opacity,
}) {
  const theme = useTheme();
  const resolvedMode =
    (mode || theme?.palette?.mode || 'light') === 'dark' ? 'dark' : 'light';
  const role = decideRole(prefer, id);
  const resolvedSpriteUrl = resolveSpriteUrl({
    role,
    mode: resolvedMode,
    spriteUrl,
    spriteUrls,
  });

  const sameOrigin = (() => {
    if (!resolvedSpriteUrl || typeof window === 'undefined') return true;
    try { return new URL(resolvedSpriteUrl, window.location.href).origin === window.location.origin; }
    catch { return true; }
  })();

  const symbolId = buildSymbolId(role, resolvedMode, id);
  const href = sameOrigin ? `${resolvedSpriteUrl}#${symbolId}` : `#${symbolId}`;
  const svgStyle = opacity == null ? style : { ...style, opacity };
  const renderWidth = width ?? size;
  const renderHeight = height ?? size;

  // NEW: bump this when the sprite or the target symbol becomes available
  const [readyTick, setReadyTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // If we’re cross-origin, ensure the sprite is injected, then tick.
    if (!sameOrigin && resolvedSpriteUrl) {
      injectSpriteOnce(resolvedSpriteUrl).then(() => {
        if (!cancelled) setReadyTick(t => t + 1);
      });
    }

    // If the bank will be populated *after* this effect, also observe it
    // so we can tick exactly when our symbol shows up.
    if (!sameOrigin) {
      const bank = ensureBank();
      if (bank && !hasSymbolInBank(symbolId)) {
        const mo = new MutationObserver(() => {
          if (hasSymbolInBank(symbolId)) {
            setReadyTick(t => t + 1);
          }
        });
        mo.observe(bank, { childList: true, subtree: true });
        return () => { cancelled = true; mo.disconnect(); };
      }
    }

    return () => { cancelled = true; };
  }, [resolvedSpriteUrl, sameOrigin, symbolId]);

  // Only show text fallback while cross-origin sprite isn’t ready for THIS symbol
  const needFallback =
    fallback === 'text' &&
    !sameOrigin &&
    typeof document !== 'undefined' &&
    !hasSymbolInBank(symbolId);

  if (needFallback) {
    return (
      <TextPlaceholder
        size={size}
        width={renderWidth}
        height={renderHeight}
        label={label}
        className={className}
        style={svgStyle}
        title={title}
      />
    );
  }

  return (
    <svg
      key={`${symbolId}-${readyTick}`}  // ensures remount when readyTick changes
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      width={renderWidth}
      height={renderHeight}
      className={className}
      style={svgStyle}
      focusable="false"
    >
      {title && <title>{title}</title>}
      <use href={href} xlinkHref={href} />
    </svg>
  );
}

/* -------------------------------- TickerIcon ------------------------------ */
export default function TickerIcon({
  ticker,
  prefer = 'icons',   // use 'logos' on your carousel
  sourcePriority = 'sprite-first',
  mode,               // e.g. mode="dark" for inverted tooltips
  spriteUrl,
  spriteUrls,
  fallback = 'none',
  size = 24,
  width,
  height,
  title,
  label,
  className,
  style,
  opacity,
}) {
  const theme = useTheme();
  const resolvedMode =
    (mode || theme?.palette?.mode || 'light') === 'dark' ? 'dark' : 'light';
  const id = spriteIdFromTicker(ticker);
  const role = decideRole(prefer, id);
  const cdnUrl = resolveCompanyAssetUrl({
    ticker,
    prefer,
    mode: resolvedMode,
  });
  const [cdnFailed, setCdnFailed] = useState(false);
  const accessibleLabel = getCompanyDisplayName(ticker, label);
  const normalizedTicker = String(ticker || '').trim();
  const safeTitle = title && !(
    isPrivateCompanyIdentifier(normalizedTicker)
    && String(title).toUpperCase().includes(normalizedTicker.toUpperCase())
  )
    ? title
    : `${accessibleLabel} logo`;

  useEffect(() => {
    setCdnFailed(false);
  }, [cdnUrl]);

  if (sourcePriority === 'sprite-first' && hasSpriteId(role, id)) {
    return (
      <SpriteIcon
        id={id}
        prefer={prefer}
        mode={resolvedMode}
        spriteUrl={spriteUrl}
        spriteUrls={spriteUrls}
        fallback={fallback}
        size={size}
        width={width}
        height={height}
        title={safeTitle}
        label={accessibleLabel}
        className={className}
        style={style}
        opacity={opacity}
      />
    );
  }

  if (cdnUrl && !cdnFailed) {
    return (
      <CdnTickerAsset
        url={cdnUrl}
        size={size}
        width={width}
        height={height}
        title={safeTitle}
        className={className}
        style={style}
        opacity={opacity}
        onError={() => setCdnFailed(true)}
      />
    );
  }

  return (
    <SpriteIcon
      id={id}
      prefer={prefer}
      mode={resolvedMode}
      spriteUrl={spriteUrl}
      spriteUrls={spriteUrls}
      fallback={fallback}
      size={size}
      width={width}
      height={height}
      title={safeTitle}
      label={accessibleLabel}
      className={className}
      style={style}
      opacity={opacity}
    />
  );
}

export { assetTickerFromTicker, resolveCompanyAssetUrl, spriteIdFromTicker, stripSpriteStyleElements };
